if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);

            // --- INJECT OUR SLIDER INITIALIZER ---
            // This triggers exactly when the content has loaded inside this specific modal
            setTimeout(() => {
              initializeQuickAddGallery(this);
            }, 150);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        // Preserves the product-modal element inside Quick Add so you can click to zoom
        // const productModal = productElement.querySelector('product-modal');
        // if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

// --- GLOBAL QUICK ADD SLIDER & KEYBOARD INITIALIZER ---
function initializeQuickAddGallery(quickAdd) {
  const container = quickAdd.querySelector('.product__media-list');
  if (!container) return;

  const items = Array.from(container.children).filter(item => !item.classList.contains('quick-add-hidden'));
  if (items.length <= 1) return;

  if (quickAdd.querySelector('.quick-add-custom-nav')) return; // Prevents duplicate arrows

  // Create Navigation Arrows for Quick Add Images
  const prevBtn = document.createElement('button');
  prevBtn.className = 'product-media-modal__nav quick-add-custom-nav quick-add-custom-nav--prev';
  prevBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>`;
  prevBtn.type = 'button';

  const nextBtn = document.createElement('button');
  nextBtn.className = 'product-media-modal__nav quick-add-custom-nav quick-add-custom-nav--next';
  nextBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>`;
  nextBtn.type = 'button';

  // Pin the custom arrows specifically to the main image slider component (ignores thumbnail row height)
  const mainSlider = quickAdd.querySelector('slider-component:not(.thumbnail-slider)') || quickAdd.querySelector('.product__media-list');
  const targetParent = mainSlider || quickAdd.querySelector('.product__media-wrapper');
  if (targetParent) {
    targetParent.style.position = 'relative';
    targetParent.appendChild(prevBtn);
    targetParent.appendChild(nextBtn);
  }

  let currentIndex = 0;
  function scrollToIndex(index) {
    if (index < 0 || index >= items.length) return;
    currentIndex = index;
    items[currentIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    updateCounter(currentIndex);
  }

  prevBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (currentIndex > 0) scrollToIndex(currentIndex - 1);
  });

  nextBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (currentIndex < items.length - 1) scrollToIndex(currentIndex + 1);
  });

  // Update native "1 of X" text counter
  const counterElement = quickAdd.querySelector('.slider-counter--current');
  function updateCounter(index) {
    if (counterElement) {
      counterElement.textContent = index + 1;
    }
  }

  // Keyboard Controls (Quick Add specific)
  const handleQuickAddKeyDown = (event) => {
    if (!quickAdd.hasAttribute('open')) {
      window.removeEventListener('keydown', handleQuickAddKeyDown);
      return;
    }
    if (event.key === 'ArrowRight') {
      if (currentIndex < items.length - 1) scrollToIndex(currentIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      if (currentIndex > 0) scrollToIndex(currentIndex - 1);
    }
  };
  window.addEventListener('keydown', handleQuickAddKeyDown);

  // Wheel support inside Quick Add
  container.addEventListener('wheel', (e) => {
    if (e.deltaY !== 0) {
      e.preventDefault();
      container.scrollBy({ left: e.deltaY * 1.2, behavior: 'auto' });
    }
  }, { passive: false });

  // Swipe/drag tracking inside Quick Add
  const scrollObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const index = items.indexOf(entry.target);
        if (index !== -1) {
          currentIndex = index;
          updateCounter(index);
        }
      }
    });
  }, { root: container, threshold: 0.5 });

  items.forEach(item => scrollObserver.observe(item));
}